/**
 * Toile Product Configuration
 *
 * The third product: general-purpose image generation one twists from a preset or from
 * scratch — `continue(basis, direction)`. This file is the ground only. Copy, funnel overrides
 * and commerce arrive with the surfaces; what is here is what root.tsx reads to render a
 * document at all.
 *
 * Identity: thinking/design/toile/toile_identity.md (status: current-canon). Dark is a
 * commitment — there is no light mode, so colorScheme is 'dark' with no counterpart.
 */

import type {FunnelOverrides} from '@core/products/funnel'
import {surfaceOgImageUrl} from '@core/og/cardUrls'

export const toileConfig = {
  id: 'toile',
  name: 'toile',
  domain: 'toile.pictures',

  // SEO & Meta. The description is the home's and the home card's (PROPOSITION.md: the catalogue, the change in a
  // few words, the blank tile, free) and the default of every page without its own.
  meta: {
    title: 'Toile — free AI image generator',
    description:
      'A wall of ready pictures — coloring pages, wallpapers, stickers, logos, patterns and more. Take any ' +
      'picture and change it with a few words, or start from nothing. Free.',
    url: 'https://toile.pictures',
    keywords: 'AI image generator, coloring pages, stickers, logos, wallpapers, free AI pictures',
    ogImage: surfaceOgImageUrl('https://toile.pictures', 'home'),
    ogImageType: 'image/png' as const,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    themeColor: '#141210', // Stage — the ground token
    colorScheme: 'dark' as const,
  },

  // Backend Services — internal service addresses. Server-only: @product/toile/config.server.ts
  // (toileServerConfig.services, full history comment there). Never on this object —
  // toile/config.ts is imported directly by client components (Footer, …) and this whole module
  // is serialized into every page's hydration script by root.tsx's loader.

  // Feature Flags
  features: {
    streamingGeneration: false,
    examples: false,
  },

  // Free tools (`@core/tools`): anonymous runs per tool before sign-in is asked, the same allowance
  // narcis gives.
  freeToolUses: 5,

  // Contact
  // Sign-in methods beside Google and the email link (`@core/auth/signInMethods`).
  signIn: {facebook: true, googleOneTap: true},

  contact: {
    email: 'contact@narcis.ai',
  },

  // Funnel — the anonymous session's one token is the wall's free pick (plan
  // 2026_10_25_toile_first_take_free.md): spent by a signed-out visitor's first Generate, no bonus at sign-up.
  funnel: {freeTokens: 1, signupBonus: 0} satisfies FunnelOverrides,

  // Session cookie domain and the commerce processor flag are server-only:
  // @product/toile/config.server.ts (toileServerConfig.session / .commerce), same reason as
  // `services` above.
} as const

export type ToileConfig = typeof toileConfig

/**
 * Base URL for pack sample images (originals). Same shape as `NARCIS_PACK_SAMPLES_BASE_URL` and
 * `OVID_PACK_SAMPLES_BASE_URL`: `<base>/<slug>/<file>.webp`. The toile.pictures CloudFront
 * distribution (`cloudfront-toile.tf`) routes `/pack-samples/*` to the shared content bucket by OAC,
 * same-origin, so this resolves in both dev and prod without an env override. The shared
 * `pack-samples` prefix (narcis + ovid) is never written to from here — see the operator note in
 * `thinking/threads/general_generator.md`.
 */
export const TOILE_PACK_SAMPLES_BASE_URL =
    process.env.TOILE_PACK_SAMPLES_BASE_URL || 'https://toile.pictures/pack-samples/toile'
